import React, { useState } from "react";
import { Brain, FolderKanban } from "lucide-react";

const card = "rounded-2xl border border-[#00ff90]/20 bg-[#080808] shadow-[0_0_24px_#00ff9010]";
const NEON = "#00ff90";

export default function ProjectsWidget({ data = {}, period, loading, onAskKira }) {
  const [tab, setTab] = useState("overview");

  const projects   = data.projects_summary || [];
  const byDept     = data.by_department    || [];

  return (
    <div className={`${card} flex flex-col`}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
        <div className="flex items-center gap-2">
          <FolderKanban size={14} className="text-[#00ff90]/60" />
          <span className="text-sm font-semibold text-white/80">Projects & Tasks</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => onAskKira(data, "Analyse project portfolio health: completion rates, overdue tasks, workload distribution. Identify at-risk projects and recommend actions.")}
            className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-[#00ff90]/20 bg-[#00ff90]/5 text-[#00ff90]/60 hover:text-[#00ff90] transition-colors">
            <Brain size={11} /> KIRA
          </button>
        </div>
      </div>

      <div className="flex gap-1 px-4 pt-3">
        {["overview","projects","departments"].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`text-xs px-3 py-1.5 rounded-lg transition-colors capitalize ${
              tab === t ? "bg-[#00ff90]/10 text-[#00ff90] border border-[#00ff90]/20" : "text-white/30 hover:text-white/60"
            }`}>{t}</button>
        ))}
      </div>

      <div className="p-5 space-y-4">
        {loading ? (
          <div className="grid grid-cols-4 gap-3">
            {[1,2,3,4].map(i => <div key={i} className="h-20 rounded-lg bg-white/5 animate-pulse" />)}
          </div>
        ) : (
          <>
          {tab === "overview" && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: "Active Projects",  value: data.active_projects ?? "—",     color: NEON },
                  { label: "Task Completion",  value: data.task_completion ? `${data.task_completion}%` : "—", color: "#60a5fa" },
                  { label: "Overdue Tasks",    value: data.overdue_tasks ?? "—",       color: data.overdue_tasks > 0 ? "#f87171" : "#34d399" },
                  { label: "Unassigned Tasks", value: data.unassigned_tasks ?? "—",    color: data.unassigned_tasks > 0 ? "#fbbf24" : "#34d399" },
                ].map(({ label, value, color }) => (
                  <div key={label} className="rounded-xl border border-white/5 bg-white/3 p-3 text-center">
                    <p className="text-xl font-bold font-mono" style={{ color }}>{value}</p>
                    <p className="text-xs text-white/30 mt-0.5">{label}</p>
                  </div>
                ))}
              </div>

              {/* Project health quick view */}
              {projects.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {projects.slice(0, 6).map((p, i) => {
                    const health = p.completion >= 80 ? "healthy" : p.completion >= 50 ? "at_risk" : "behind";
                    const color  = health === "healthy" ? NEON : health === "at_risk" ? "#fbbf24" : "#f87171";
                    return (
                      <div key={i} className="rounded-xl border border-white/5 bg-white/3 p-3">
                        <p className="text-xs text-white/60 font-medium truncate mb-2">{p.name}</p>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-mono" style={{ color }}>{p.completion}%</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${
                            health === "healthy" ? "text-[#00ff90]/60 border-[#00ff90]/15" :
                            health === "at_risk" ? "text-yellow-400/60 border-yellow-500/15" :
                            "text-red-400/60 border-red-500/15"
                          }`}>{health.replace("_"," ")}</span>
                        </div>
                        <div className="h-1 rounded-full bg-white/5">
                          <div className="h-full rounded-full" style={{ width: `${p.completion}%`, background: `${color}60` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {tab === "projects" && (
            <div className="space-y-2">
              {projects.length === 0 ? <p className="text-white/20 text-sm italic text-center py-6">No project data.</p>
              : projects.map((p, i) => (
                <div key={i} className="rounded-xl border border-white/5 bg-white/3 px-4 py-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-white/70 font-medium">{p.name}</span>
                    <div className="flex gap-3 text-xs font-mono text-white/40">
                      <span className="text-[#00ff90]/60">{p.completion}%</span>
                      {p.overdue > 0 && <span className="text-red-400/60">{p.overdue} overdue</span>}
                      <span>{p.tasks_total} tasks</span>
                    </div>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/5">
                    <div className="h-full rounded-full"
                      style={{ width: `${p.completion}%`, background: p.completion >= 80 ? `${NEON}60` : p.completion >= 50 ? "#fbbf2460" : "#f8717160" }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "departments" && (
            <div className="space-y-2">
              {byDept.length === 0 ? <p className="text-white/20 text-sm italic text-center py-6">No data.</p>
              : byDept.map((d, i) => (
                <div key={i} className="flex items-center justify-between rounded-xl border border-white/5 bg-white/3 px-4 py-3">
                  <span className="text-sm text-white/60">{d.dept}</span>
                  <div className="flex gap-4 text-xs font-mono">
                    <span className="text-[#00ff90]/60">{d.completion}% done</span>
                    {d.overdue > 0 && <span className="text-red-400/60">{d.overdue} overdue</span>}
                    <span className="text-white/30">{d.tasks} tasks</span>
                  </div>
                </div>
              ))}
            </div>
          )}
          </>
        )}
      </div>
    </div>
  );
}